import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { PageType } from '@unifyapps/defs/types/pageType';
import type { User } from '@unifyapps/network/generated/models/user';
import { useMemo } from 'react';
import { getConditionalPropertyValue } from '../../../utils/conditionalValues';
import type { DeviceContextInStore } from '../../stores/app/GlobalAppStateStore/types';
import InterfaceHelper from '../../stores/InterfaceStore/helper/InterfaceHelper';
import { getNoCodeBulkComputedData } from '../../utils/getNoCodeComputeData';
import { getShouldEvaluateResult } from '../../utils/getShouldEvaluateResult';
import { buildAppLevelComputeContext } from '../computeContext/utils/buildAppLevelComputeContext';
import { getConditionEvaluationFunc } from '../useComputeConditionEvaluationGetter';
import { useGetUserContext } from '../useGetUserContext';

export function getResolvedDefaultPageId({
  interfaceRecord,
  userContext,
  deviceContext,
  envVariables,
  localStorage,
  pageType = PageType.Private,
  activeAppVariantKey,
}: {
  interfaceRecord: InterfaceEntity | undefined;
  envVariables: Record<string, string>;
  userContext: User | undefined;
  deviceContext?: DeviceContextInStore;
  localStorage?: Record<string, unknown>;
  pageType?: PageType;
  activeAppVariantKey?: string;
}) {
  if (!interfaceRecord) {
    return undefined;
  }
  // `defaultPublicPageId` is the "logged out user" landing and only
  // applies to private apps — the builder hides it for public apps, whose
  // single landing is `defaultPageId` ("Default Page"). So a public
  // interface always resolves `defaultPageId` regardless of pageType /
  // auth state, matching the web root pipeline.
  const navigation = interfaceRecord?.properties?.navigation;
  const useLoggedOutLanding =
    pageType === PageType.Public && !InterfaceHelper.isPublicInterface(interfaceRecord);
  const defaultPageId = useLoggedOutLanding
    ? navigation?.defaultPublicPageId
    : navigation?.defaultPageId;

  if (!defaultPageId) {
    return undefined;
  }

  const context = buildAppLevelComputeContext({
    interfaceRecord,
    userContext,
    deviceContext,
    envVariables,
    localStorage,
    activeAppVariantKey,
  });

  const shouldEvaluateResult = getShouldEvaluateResult(interfaceRecord.properties.flags);

  const computedFilterValue = getConditionEvaluationFunc<{ defaultPageId: string }>(
    { defaultPageId },
    context,
    <T extends Record<string, string>>(data: T, ctx: Record<string, unknown>) =>
      getNoCodeBulkComputedData<T>(data, ctx, shouldEvaluateResult),
  );

  return getConditionalPropertyValue<string>({
    value: computedFilterValue.defaultPageId,
  });
}

export function useResolvePageId({
  interfaceRecord,
  envVariables,
  isLoadingEnvVariables,
  localStorage,
}: {
  interfaceRecord: InterfaceEntity | undefined;
  envVariables: Record<string, string>;
  isLoadingEnvVariables: boolean;
  localStorage?: Record<string, unknown>;
}) {
  const { userContext, deviceContext } = useGetUserContext();

  const pageId = useMemo(() => {
    if (isLoadingEnvVariables) return undefined;

    return getResolvedDefaultPageId({
      interfaceRecord,
      envVariables,
      userContext,
      deviceContext,
      localStorage,
    });
  }, [
    interfaceRecord,
    userContext,
    deviceContext,
    envVariables,
    isLoadingEnvVariables,
    localStorage,
  ]);

  return { pageId };
}
